import React, { useState, useEffect, useCallback, useMemo } from "react";
import Btn, { FormBtn } from "components/Btn";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import Impressions from "./Impressions";
import CampaignForm from "./CampaignForm";
import { withTranslation } from "react-i18next";
import { apiRequest } from "store/Misc/apiRequest";
import dayjs from "dayjs";
import useCampaign from "hooks/useCampaign";
import { mutate } from "swr";
import { toast } from "react-toastify";

export default withTranslation()(function CreateCampaign({ t }) {
  const { currentPage } = useCampaign();
  const [step, setStep] = useState(0);
  const [submitState, setSubmitState] = useState(null);
  const methods = useForm({ mode: "all" });
  const values = useWatch({ control: methods.control });

  const { reset, handleSubmit, getValues, watch } = methods;

  useEffect(() => {
    reset({
      type: "",
      title: "",
      media: "",
      description: "",
      message: "",
      callback_url: "",
      promo_frequency_id: "",
      budget: "",
      interests: [],
      professions: [],
      schedules: [],
      start: "",
      end: "",
      lat: "",
      lng: "",
    });
  }, [reset]);

  const labels = useMemo(
    () => [
      { title: t("promo.create.info") },
      { title: t("promo.create.audience") },
      { title: t("promo.create.budget") },
    ],
    [t]
  );

  const changeStep = useCallback(
    (direction) =>
      setStep((prev) => (direction === "next" ? prev + 1 : prev - 1)),
    []
  );

  /* ------------------ Validation helpers ------------------ */
  const isNonEmpty = useCallback(
    (value) => String(value ?? "").trim().length > 1,
    []
  );

  const isValidUrl = useCallback((url) => {
    const pattern =
      /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i;
    return pattern.test(url);
  }, []);

  
  /* ------------------ Button state (MEMOIZED) ------------------ */
const btnDisabled = useMemo(() => {
  if (!values) return true;

  if (step === 0) {
    return !(
      isNonEmpty(values.title) &&
      isNonEmpty(values.type) &&
      isNonEmpty(values.message) &&
      isNonEmpty(values.description) &&
      isValidUrl(values.callback_url)
    );
  }

  if (step === 1) {
    return false;
  }

  if (step === 2) {
    return !(
      isNonEmpty(values.budget) &&
      values.schedules?.length > 0 &&
      typeof values.media !== "string"
    );
  }

  return true;
}, [step, values, isNonEmpty, isValidUrl]);


  console.log(
    { btnDisabled, step },
    "title",
    isNonEmpty(watch().title),
    "type",
    isNonEmpty(watch().type),
    "message",
    isNonEmpty(watch().message),
    "description",
    isNonEmpty(watch().description),
    "callback_url",
    isValidUrl(watch().callback_url)
  );

  /* ------------------ Submit ------------------ */
  const submit = async () => {
    setSubmitState("loading");

    const { media, interests, professions, schedules, start, end, ...payload } =
      getValues();

    const formData = new FormData();

    Object.entries(payload).forEach(([k, v]) => v && formData.append(k, v));

    formData.append("media", media[0]);
    formData.append("start", dayjs(start).format("YYYY-MM-DD"));
    formData.append("end", dayjs(end).format("YYYY-MM-DD"));
    formData.append("interests", JSON.stringify(interests));
    formData.append("professions", JSON.stringify(professions));
    formData.append("schedules", JSON.stringify(schedules));

    try {
      await apiRequest({
        method: "post",
        url: "promos/campaigns",
        payload: formData,
      });

      toast.success("Successfully created campaign");
      mutate(`promos/campaigns?page=${currentPage}`);
      window.location.assign("/promo/campaign");
    } catch (err) {
      setSubmitState("error");
      const errors = JSON.parse(err.message.replace("Error: ", ""));

      Object.values(errors)
        .flat()
        .forEach((msg) => toast.error(msg));
    } finally {
      setSubmitState(null);
    }
  };

  const isLastStep = step + 1 === labels.length;

  return (
    <div className="w-full grid lg:grid-cols-3 gap-8 mb-10 shadow-lg">
      <FormProvider {...methods}>
        <form
          className="order-2 lg:order-1 lg:col-span-2 bg-white rounded-xl py-8 p-4 lg:p-8 pb-16"
          onSubmit={handleSubmit(submit)}
        >
          <header className="flex justify-between mb-8 font-bold text-secondary">
            <span>{labels[step].title}</span>
            <span>{`STEP ${step + 1} OF ${labels.length}`}</span>
          </header>

          <CampaignForm step={step} />

          <div className="flex gap-5">
            {step > 0 && (
              <Btn
                content={t("misc.back")}
                click={() => changeStep("back")}
                className="border-2 border-primary h-12"
              />
            )}

            {isLastStep ? (
              <FormBtn
                disabled={btnDisabled || submitState === "loading"}
                content={
                  submitState === "loading" ? "Submitting..." : t("misc.submit")
                }
                className="bg-primary h-12 text-white"
              />
            ) : (
              <Btn
                content={t("misc.next")}
                disabled={btnDisabled}
                click={() => changeStep("next")}
                className="bg-primary h-12 text-white"
              />
            )}
          </div>
        </form>

        <Impressions />
      </FormProvider>
    </div>
  );
});
